import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Banknote, Wallet, Lock, ArrowDownToLine, ShieldCheck, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { BACKEND_URL } from '../lib/api';

/**
 * Owner-only — banking details (with masking after save), balance card,
 * payout request flow, and the owner's payout history.
 */
const STATUS_TONE = {
  pending:  'bg-amber-50 text-amber-800 border-amber-200',
  approved: 'bg-blue-50 text-blue-800 border-blue-200',
  paid:     'bg-emerald-50 text-emerald-800 border-emerald-200',
  rejected: 'bg-rose-50 text-rose-800 border-rose-200',
};

const PayoutsSection = ({ onBankingSaved }) => {
  const [banking, setBanking] = useState(null);
  const [editing, setEditing] = useState(false);
  const [bankForm, setBankForm] = useState({ account_holder: '', bank_name: '', branch_code: '', account_number: '', account_type: '' });
  const [savingBank, setSavingBank] = useState(false);

  const [balance, setBalance] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [reqAmount, setReqAmount] = useState('');
  const [reqNotes, setReqNotes] = useState('');
  const [submittingReq, setSubmittingReq] = useState(false);

  const fetchAll = async () => {
    try {
      const [bk, bl, ps, pv] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/organiser/banking`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/organiser/balance`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/organiser/payouts`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/organiser/settlement-preview`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setBanking(bk.data?.banking || null);
      setBalance(bl.data || null);
      setPayouts(ps.data?.payouts || []);
      setPreview(pv.data || null);
    } catch {
      // silent
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const startEditBank = () => {
    setBankForm({
      account_holder: banking?.account_holder || '',
      bank_name: banking?.bank_name || '',
      branch_code: banking?.branch_code || '',
      account_number: '',
      account_type: banking?.account_type || '',
    });
    setEditing(true);
  };

  const saveBank = async (e) => {
    e.preventDefault();
    setSavingBank(true);
    try {
      await axios.post(`${BACKEND_URL}/api/organiser/banking`, bankForm, { withCredentials: true });
      toast.success('Banking details saved securely.');
      setEditing(false);
      setBankForm({ account_holder: '', bank_name: '', branch_code: '', account_number: '', account_type: '' });
      fetchAll();
      if (typeof onBankingSaved === 'function') onBankingSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save banking details.');
    } finally {
      setSavingBank(false);
    }
  };

  const requestPayout = async (e) => {
    e.preventDefault();
    const amt = parseFloat(reqAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount.'); return; }
    setSubmittingReq(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/organiser/payouts`,
        { amount: amt, notes: reqNotes || null },
        { withCredentials: true },
      );
      toast.success('Withdrawal request submitted. Check your email for confirmation — we will process it within 1–2 business days.');
      setReqAmount('');
      setReqNotes('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit payout request.');
    } finally {
      setSubmittingReq(false);
    }
  };

  const available = balance?.available || 0;

  return (
    <div data-testid="payouts-section" className="bg-white rounded-xl shadow-sm border border-[#E5E9E6] mt-8">
      <div className="p-6 border-b border-[#E5E9E6] flex items-center gap-3">
        <Wallet className="w-5 h-5 text-[#1F3D2B]" />
        <h2 className="text-2xl font-semibold text-[#112318]" style={{ fontFamily: 'Playfair Display, serif' }}>
          Payouts
        </h2>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Balance card */}
        <div className="bg-gradient-to-br from-[#1F3D2B] to-[#0f2014] text-white rounded-2xl p-6 shadow-sm">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A27A] mb-2">Available to withdraw</p>
          <p data-testid="balance-available" className="text-4xl font-semibold mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
            R{available.toFixed(2)}
          </p>
          <p className="text-xs text-white/60 mb-5">From events that have already taken place.</p>

          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between text-white/80">
              <span>Past-event revenue</span>
              <span>R{(balance?.lifetime_revenue_past || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-white/80">
              <span>Pending payouts (held)</span>
              <span>R{(balance?.pending_payouts || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-white/60">
              <span>Future-event revenue (locked)</span>
              <span>R{(balance?.lifetime_revenue_future || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Banking + request */}
        <div className="space-y-5">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#4A5D50] mb-2 flex items-center gap-1.5">
              <Banknote className="w-3.5 h-3.5" /> Banking details
            </p>
            {!banking || editing ? (
              <form onSubmit={saveBank} className="space-y-3 bg-stone-50 border border-stone-200 rounded-xl p-4">
                <div>
                  <Label className="text-xs">Account holder</Label>
                  <Input data-testid="bank-account-holder" value={bankForm.account_holder} onChange={(e) => setBankForm({...bankForm, account_holder: e.target.value})} required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Bank</Label>
                    <Input data-testid="bank-name" value={bankForm.bank_name} onChange={(e) => setBankForm({...bankForm, bank_name: e.target.value})} placeholder="e.g. ABSA" required />
                  </div>
                  <div>
                    <Label className="text-xs">Branch code</Label>
                    <Input data-testid="bank-branch-code" value={bankForm.branch_code} onChange={(e) => setBankForm({...bankForm, branch_code: e.target.value})} placeholder="6 digits" required />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Account number</Label>
                  <Input data-testid="bank-account-number" value={bankForm.account_number} onChange={(e) => setBankForm({...bankForm, account_number: e.target.value})} placeholder="Will be masked after saving" required />
                </div>
                <div>
                  <Label className="text-xs">Account type</Label>
                  <select
                    data-testid="bank-account-type"
                    value={bankForm.account_type || ''}
                    onChange={(e) => setBankForm({ ...bankForm, account_type: e.target.value })}
                    className="w-full border border-[#E5E9E6] rounded-md px-3 py-2 text-sm bg-white"
                  >
                    <option value="">Select type (optional)</option>
                    <option value="cheque">Cheque</option>
                    <option value="savings">Savings</option>
                    <option value="transmission">Transmission</option>
                    <option value="business">Business</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  {banking && (
                    <Button type="button" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                  )}
                  <Button type="submit" disabled={savingBank} className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full" data-testid="bank-save">
                    <Lock className="w-4 h-4 mr-2" />
                    {savingBank ? 'Saving…' : 'Save securely'}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="bg-stone-50 border border-stone-200 rounded-xl p-4 text-sm space-y-1">
                <p className="text-[#112318] font-medium">{banking.account_holder}</p>
                <p className="text-stone-600">{banking.bank_name} · Branch {banking.branch_code}{banking.account_type ? ` · ${banking.account_type.charAt(0).toUpperCase()}${banking.account_type.slice(1)}` : ''}</p>
                <p data-testid="bank-account-masked" className="font-mono text-stone-700">{banking.account_number_masked}</p>
                <button type="button" onClick={startEditBank} data-testid="bank-edit" className="text-xs text-[#1F3D2B] underline hover:no-underline mt-1">Edit details</button>
              </div>
            )}
          </div>

          {/* Banking-required notice (shown only when no banking saved yet) */}
          {!banking && !editing && (
            <div
              className="bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-4 py-3 text-xs leading-relaxed"
              data-testid="banking-required-notice"
            >
              Please add your banking details before requesting a withdrawal.
            </div>
          )}

          {/* Request payout */}
          {banking && !editing && (
            <form onSubmit={requestPayout} className="space-y-3" data-testid="withdraw-funds-form">
              <p className="text-xs uppercase tracking-wider text-[#4A5D50] flex items-center gap-1.5">
                <ArrowDownToLine className="w-3.5 h-3.5" /> Withdraw funds
              </p>

              {preview && (preview.totals?.gross > 0 || (preview.rows && preview.rows.length > 0)) && (
                <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-xs space-y-1" data-testid="withdrawal-breakdown-preview">
                  <p className="text-[10px] uppercase tracking-wider text-[#4A5D50] mb-1.5">Withdrawal breakdown</p>
                  <div className="flex justify-between text-stone-600">
                    <span>Gross sales</span>
                    <span>R{(preview.totals?.gross || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-stone-600">
                    <span>VinePass fees</span>
                    <span>−R{(preview.totals?.fees || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-stone-600">
                    <span>Refunds</span>
                    <span>−R{(balance?.approved_refunds_deducted || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-medium text-[#1F3D2B] border-t border-stone-200 pt-1.5 mt-1.5">
                    <span>Net withdrawable</span>
                    <span>R{available.toFixed(2)}</span>
                  </div>
                  {preview.rows && preview.rows.length > 0 && (
                    <p className="text-[11px] text-stone-500 pt-1">{preview.rows.length} past event{preview.rows.length === 1 ? '' : 's'} included.</p>
                  )}
                </div>
              )}

              <div>
                <Label className="text-xs">Amount (ZAR)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max={available}
                  value={reqAmount}
                  onChange={(e) => setReqAmount(e.target.value)}
                  data-testid="payout-amount"
                  placeholder={`Up to R${available.toFixed(2)}`}
                  required
                />
              </div>
              <div>
                <Label className="text-xs">Notes (optional)</Label>
                <Input value={reqNotes} onChange={(e) => setReqNotes(e.target.value)} data-testid="payout-notes" />
              </div>
              <Button
                type="submit"
                disabled={submittingReq || available <= 0}
                className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-6 text-base font-medium shadow-md"
                data-testid="payout-submit"
              >
                <ArrowDownToLine className="w-4 h-4 mr-2" />
                {available <= 0
                  ? 'No funds available yet'
                  : (submittingReq ? 'Submitting…' : `Withdraw Funds · R${(parseFloat(reqAmount) || 0).toFixed(2)}`)}
              </Button>
              <p className="text-[11px] text-stone-500 text-center">
                We&apos;ll email you a confirmation and notify the VinePass finance team.
              </p>
            </form>
          )}
        </div>
      </div>

      {/* Payout history */}
      {payouts.length > 0 && (
        <div className="border-t border-[#E5E9E6] p-6">
          <p className="text-xs uppercase tracking-wider text-[#4A5D50] mb-3">Payout history</p>
          <div className="divide-y divide-stone-100">
            {payouts.map((p) => (
              <div key={p.payout_id} data-testid={`payout-row-${p.status}`} className="py-3 flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-[#112318]">R{p.amount.toFixed(2)}</p>
                    <p className="text-xs text-stone-500">Requested {new Date(p.requested_at).toLocaleDateString('en-ZA')}</p>
                  </div>
                  <span className={`px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wider rounded-full border ${STATUS_TONE[p.status] || ''}`}>
                    {p.status === 'paid' ? <ShieldCheck className="inline w-3 h-3 mr-1" /> : p.status === 'pending' ? <Clock className="inline w-3 h-3 mr-1" /> : null}
                    {p.status}
                  </span>
                </div>
                {p.status === 'approved' && (
                  <p
                    className="text-[11px] text-[#4A5D50] bg-[#F4F6F3] border border-[#E5E9E6] rounded-md px-2.5 py-1.5 leading-relaxed"
                    data-testid={`payout-approved-note-${p.payout_id}`}
                  >
                    Approved — being processed to your registered bank account. Please allow 1&ndash;2 business days for the funds to reflect, depending on your bank&rsquo;s processing times.
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutsSection;
